import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { appendWorkflowName, StageBackButton, useFlow } from '../components/StageWrapper';
import {
  getComplexServicesIgd,
  saveComplexServicesIgd,
  type ComplexServicesArtifactStageData,
} from '../api/complexServicesFunctions';
import { toUserFacingError } from '../utils/userFacingError';
import SimpleStageTour, { type SimpleStageTourHandle } from '../components/tour/SimpleStageTour';
import { steps } from './ComplexServicesIgd.tour';
import { StageTourButton } from '../components/StageTourButton';

/** Focused, always-enabled Complex Services IGD checklist (storage stage 617). */
export interface ComplexServicesIgdProps {
  flowId?: string | null;
  initialData?: unknown;
  onComplete: (data?: unknown) => void;
}

type Answer = '' | 'yes' | 'no';

interface ChecklistSection {
  answer: Answer;
  items: string[];
}

interface ArtifactRecord {
  artifact_id?: string;
  filename?: string;
  source?: string;
  created_at?: string;
}

interface IgdData extends ComplexServicesArtifactStageData {
  section1?: Partial<ChecklistSection>;
  section2?: Partial<ChecklistSection>;
  section3?: Partial<ChecklistSection>;
  section4?: Partial<ChecklistSection>;
  artifact_complete?: boolean;
  artifact_status?: string;
  provenance?: { generator?: string };
}

const SECTIONS = [
  {
    key: 'section1',
    prompt: 'Will the workload include any inherently governmental functions?',
    items: [
      'Direction and Control of Combat and Crisis Situations',
      'DoD Civilian Authority Direction and Control',
      'Military-Unique Knowledge and Skills',
      'Military Augmentation of the Infrastructure During War',
    ],
  },
  {
    key: 'section2',
    prompt: 'Is the workload exempted from private-sector performance?',
    items: [
      'Exempted from Private Sector Performance',
      'Exempted of Combat Support and Combat Service Support due to Operational Risk',
      'Exempted of Manpower Dual-Tasked For Wartime Assignments',
      'Exempted for Esprit de Corps',
      'Exempted for Continuity of Infrastructure Operations',
      'Exempted for Civilian and Military Rotation',
      'Exempted for Civilian and Military Career Development',
      'Exempted by Law, Executive Order, Treaty or International Agreement',
      'Exempted by DoD Management Decision',
    ],
  },
  {
    key: 'section3',
    prompt: 'Will the workload be subject to private-sector performance?',
    items: [
      'Subject to Private Sector Performance',
      'Pending Restructuring of Commercial Activity',
      'Subject to Review for Public-Private Performance',
      'Non-Packageable Commercial Activity',
      'Alternative to Public-Private Performance',
    ],
  },
  {
    key: 'section4',
    prompt: 'Can the workload be considered closely associated, personal services, or a personnel shortfall?',
    items: [
      'Closely Associated with IG Functions',
      'Personal Services',
      'Personnel Shortfalls',
    ],
  },
] as const;

type SectionKey = (typeof SECTIONS)[number]['key'];
type ChecklistState = Record<SectionKey, ChecklistSection>;

const emptyChecklist = (): ChecklistState =>
  SECTIONS.reduce((result, section) => {
    result[section.key] = { answer: '', items: [] };
    return result;
  }, {} as ChecklistState);

const asSection = (value: unknown): ChecklistSection => {
  const data = value && typeof value === 'object' ? (value as Partial<ChecklistSection>) : {};
  return {
    answer: data.answer === 'yes' || data.answer === 'no' ? data.answer : '',
    items: Array.isArray(data.items) ? data.items.filter((item): item is string => typeof item === 'string') : [],
  };
};

const checklistFrom = (data: IgdData | null | undefined): ChecklistState =>
  SECTIONS.reduce((result, section) => {
    result[section.key] = asSection(data?.[section.key]);
    return result;
  }, {} as ChecklistState);

const completeArtifact = (data: IgdData | null | undefined): ArtifactRecord | null => {
  const artifact = data?.artifact;
  if (!data?.artifact_complete || data.artifact_status !== 'available') return null;
  if (!artifact || typeof artifact !== 'object') return null;
  const record = artifact as ArtifactRecord;
  return typeof record.artifact_id === 'string' && record.artifact_id ? record : null;
};

const makePayload = (checklist: ChecklistState): IgdData => ({ ...checklist });

const setRadioTestId = (testId: string) => (input: HTMLInputElement | null) => {
  if (input) input.setAttribute('data-testid', testId);
};

const ComplexServicesIgd: React.FC<ComplexServicesIgdProps> = ({ flowId, initialData, onComplete }) => {
  const tourRef = useRef<SimpleStageTourHandle>(null);
  const { flowName, isViewOnly, isSampleFlow, onSampleContinue } = useFlow();
  const seed = initialData as IgdData | undefined;
  const [checklist, setChecklist] = useState<ChecklistState>(() => checklistFrom(seed));
  const [savedData, setSavedData] = useState<IgdData | null>(() => seed ?? null);
  const [hydrating, setHydrating] = useState(() => !initialData && !!flowId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  const queuedSaveRef = useRef<{ payload: IgdData; showFailure: boolean } | null>(null);
  const saveRef = useRef<
    (nextPayload: IgdData, options: { showFailure: boolean }) => Promise<IgdData | null>
  >();

  const payload = useMemo(() => makePayload(checklist), [checklist]);
  const snapshot = useMemo(() => JSON.stringify(payload), [payload]);
  const allDeterminationsAnswered = SECTIONS.every((section) => checklist[section.key].answer !== '');
  const canContinue = !isViewOnly && allDeterminationsAnswered;
  const canDownload = !!flowId && (isViewOnly ? !!completeArtifact(savedData) : allDeterminationsAnswered);

  const applySavedData = useCallback((data: IgdData) => {
    setChecklist(checklistFrom(data));
    setSavedData(data);
    lastSavedRef.current = JSON.stringify(makePayload(checklistFrom(data)));
  }, []);

  useEffect(() => {
    lastSavedRef.current = JSON.stringify(makePayload(checklistFrom(seed)));
    // Initial data is already the App's persisted stage snapshot.
    if (seed) return;
    if (!flowId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = (await getComplexServicesIgd(flowId)) as IgdData | null;
        if (!cancelled && data) applySavedData(data);
      } catch {
        // The form remains usable as a new draft if hydration is unavailable.
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flowId, seed, applySavedData]);

  const invalidateArtifact = useCallback(() => {
    setSavedData((current) =>
      current
        ? { ...current, artifact: undefined, artifact_ref: undefined, artifact_complete: false, artifact_status: 'draft' }
        : current,
    );
  }, []);

  const setAnswer = (key: SectionKey, answer: Answer) => {
    if (isViewOnly) return;
    setChecklist((current) => ({ ...current, [key]: { ...current[key], answer } }));
    invalidateArtifact();
  };

  const toggleItem = (key: SectionKey, item: string) => {
    if (isViewOnly) return;
    setChecklist((current) => {
      const items = current[key].items.includes(item)
        ? current[key].items.filter((value) => value !== item)
        : [...current[key].items, item];
      return { ...current, [key]: { ...current[key], items } };
    });
    invalidateArtifact();
  };

  const save = useCallback(
    async (nextPayload: IgdData, { showFailure }: { showFailure: boolean }): Promise<IgdData | null> => {
      if (!flowId || isViewOnly) return null;
      if (savingRef.current) {
        // A radio click commonly blurs the previous choice. Queue the latest
        // snapshot so a fast pass through the checklist cannot lose the final
        // determination while an earlier blur-save is in flight.
        queuedSaveRef.current = { payload: nextPayload, showFailure };
        return null;
      }
      savingRef.current = true;
      setSaving(true);
      if (showFailure) setError(null);
      let result: IgdData | null = null;
      try {
        const data = (await saveComplexServicesIgd(flowId, nextPayload)) as IgdData;
        // Do not rehydrate the editable fields here: a newer blur may already
        // be queued locally. The server projection is still authoritative for
        // artifact availability.
        setSavedData(data);
        lastSavedRef.current = JSON.stringify(nextPayload);
        result = data;
      } catch (cause) {
        if (showFailure) setError(toUserFacingError(cause));
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
      const queued = queuedSaveRef.current;
      queuedSaveRef.current = null;
      if (queued && JSON.stringify(queued.payload) !== JSON.stringify(nextPayload)) {
        return (await saveRef.current?.(queued.payload, { showFailure: queued.showFailure })) ?? result;
      }
      return result;
    },
    [flowId, isViewOnly],
  );
  saveRef.current = save;

  const autoSave = useCallback(() => {
    if (!flowId || isViewOnly || savingRef.current || snapshot === lastSavedRef.current) return;
    void save(payload, { showFailure: true });
  }, [flowId, isViewOnly, payload, save, snapshot]);

  const handleDownload = async () => {
    if (!canDownload) return;
    const data = isViewOnly ? savedData : await save(payload, { showFailure: true });
    const generatedArtifact = completeArtifact(data);
    if (!generatedArtifact) {
      setError('The IGD Checklist DOCX could not be verified. Please try again.');
      return;
    }
    const download = document.createElement('a');
    download.href = `${process.env.REACT_APP_API_URL || ''}/api/complex-services/${encodeURIComponent(flowId)}/igd/artifact`;
    download.download = generatedArtifact.filename || 'IGD_Checklist.docx';
    download.click();
  };

  const handleContinue = async () => {
    if (isViewOnly) return;
    if (!canContinue) return;
    if (!flowId) {
      onComplete(savedData ?? payload);
      return;
    }
    const data = await save(payload, { showFailure: true });
    if (data) onComplete(data);
  };

  return (
    <Box onBlur={autoSave}>
      <SimpleStageTour ref={tourRef} tourKey="complex-services-617" steps={steps} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mb: 0.5 }}>
        <Typography variant="h4" data-testid="complex-services-igd-title" sx={{ flex: '1 1 12rem', minWidth: 0, overflowWrap: 'anywhere' }}>
          {appendWorkflowName('IGD Checklist', flowName)}
        </Typography>
        <StageTourButton onClick={() => tourRef.current?.replay()} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Complete the Inherently Governmental Determination once for this acquisition package. A
        verified, generated DOCX is required before you can continue.
      </Typography>

      {hydrating && <CircularProgress size={22} data-testid="complex-services-igd-hydrating" />}
      {error && <Alert severity="error" data-testid="complex-services-igd-error" sx={{ mb: 2 }}>{error}</Alert>}
      {!allDeterminationsAnswered && !isViewOnly && (
        <Alert severity="warning" data-testid="complex-services-igd-required-validation" sx={{ mb: 2 }}>
          Complete all four required Yes/No determinations to generate the IGD Checklist DOCX.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-testid="complex-services-igd-form">
        {SECTIONS.map((section, index) => {
          const value = checklist[section.key];
          const missing = !value.answer;
          return (
            <React.Fragment key={section.key}>
              {index > 0 && <Divider sx={{ my: 3 }} />}
              <FormControl
                component="fieldset"
                error={missing && !isViewOnly}
                disabled={isViewOnly}
                fullWidth
                data-testid={`complex-services-igd-${section.key}`}
              >
                <FormLabel component="legend">{index + 1}. {section.prompt}</FormLabel>
                <RadioGroup
                  row
                  value={value.answer}
                  onChange={(event) => setAnswer(section.key, event.target.value as Answer)}
                >
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio
                        disabled={isViewOnly}
                        inputRef={setRadioTestId(`complex-services-igd-${section.key}-answer-yes`)}
                      />
                    }
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={
                      <Radio
                        disabled={isViewOnly}
                        inputRef={setRadioTestId(`complex-services-igd-${section.key}-answer-no`)}
                      />
                    }
                    label="No"
                  />
                </RadioGroup>
                {missing && !isViewOnly && (
                  <FormHelperText data-testid={`complex-services-igd-${section.key}-required`}>
                    A Yes or No determination is required.
                  </FormHelperText>
                )}
                <FormGroup sx={{ ml: 1, mt: 0.5 }}>
                  {section.items.map((item) => (
                    <FormControlLabel
                      key={item}
                      label={item}
                      control={<Checkbox checked={value.items.includes(item)} onChange={() => toggleItem(section.key, item)} />}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </React.Fragment>
          );
        })}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
        <StageBackButton />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={!canDownload || saving}
            onClick={handleDownload}
            data-testid="complex-services-igd-download"
          >
            {saving ? 'Preparing…' : 'Download DOCX'}
          </Button>
          <Tooltip title={isSampleFlow || canContinue ? '' : 'Complete the checklist and persist the verified IGD artifact to continue.'}>
            <span>
              <Button
                variant="contained"
                size="large"
                disabled={!isSampleFlow && (isViewOnly || !canContinue || saving)}
                onClick={isSampleFlow ? onSampleContinue : () => void handleContinue()}
                data-testid="complex-services-igd-continue"
              >
                Continue
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default ComplexServicesIgd;
